import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import {
  Building2,
  CheckCircle2,
  Loader2,
  ArrowLeft,
  ShieldCheck,
  Mail,
  KeyRound,
  UserRound,
} from "lucide-react";

const TRUSTEE_BENEFITS = [
  "Übersicht aller zugeteilten Mandanten",
  "Offene Prüfungen: Jahresabschluss, Steuerunterlagen, Freigaben",
  "Direkt in den Mandanten wechseln",
  "Steuer-Ordner & Prüfprotokoll (GeBüV) je Mandant",
];

const BENEFITS = [
  "KI-gestützte Belegerfassung mit OCR",
  "Automatische MWST-Berechnung (CH)",
  "Doppelte Buchführung & Bankabgleich",
  "Lohnbuchhaltung inkl. Payslips",
];

/**
 * Mehrstufige Login-Page (Feb 2026):
 *   step="email"    : E-Mail eingeben → backend entscheidet code|password
 *   step="password" : nur für Super-Admin (klassisches Passwort)
 *   step="code"     : 6-stelliger Code aus E-Mail
 *   step="profile"  : Erstmaliger User füllt seinen Namen aus
 */
export function Login({ variant = "default" }) {
  const isTrustee = variant === "trustee";
  const [step, setStep] = useState("email");
  const [email, setEmailLocal] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [pendingToken, setPendingToken] = useState("");
  const [rememberDevice, setRememberDevice] = useState(true);
  const [profileName, setProfileName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [devCode, setDevCode] = useState(""); // Dev-Hilfe: SMTP nicht konfiguriert
  const [resending, setResending] = useState(false);
  const [registrationOpen, setRegistrationOpen] = useState(true);

  React.useEffect(() => {
    apiClient.get("/auth/registration-status").then((r) => {
      setRegistrationOpen(!!r.data?.enabled);
    }).catch(() => setRegistrationOpen(false));
  }, []);

  // Iter86: Hinweis anzeigen, wenn der User durch abgelaufenen Token hierher kam
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("reason") === "session-expired") {
      setInfo("Deine Sitzung ist abgelaufen. Bitte logge dich erneut ein.");
    }
  }, []);

  const navigate = useNavigate();
  const { setEmail, me, refreshMe } = useAuth();

  // Wenn User schon Token hat aber Profil unvollständig → direkt zum Profil-Step.
  // Wenn er einen kompletten Account hat → Dashboard.
  useEffect(() => {
    if (!localStorage.getItem("authEmail")) return;
    refreshMe();
  }, [refreshMe]);

  // Iter259: Mitarbeiter-Portal-User landen auf /my-time statt im Dashboard
  const goHome = React.useCallback(async (replace = false) => {
    try {
      const r = await apiClient.get("/my/portal/profile");
      if (r.data?.is_employee && !r.data?.has_company_access) {
        navigate("/my-time", { replace });
        return;
      }
    } catch { /* kein Portal-User */ }
    try {
      const t = await apiClient.get("/my/trustee/profile");
      if (isTrustee || t.data?.is_trustee) {
        navigate("/treuhand/cockpit", { replace });
        return;
      }
    } catch { /* kein Treuhänder */ }
    navigate("/", { replace });
  }, [navigate, isTrustee]);

  useEffect(() => {
    if (!me) return;
    if (me.profile_completed === false && !me.is_super_admin) {
      setEmailLocal(me.email || "");
      setStep("profile");
    } else if (me.profile_completed) {
      goHome(true);
    }
  }, [me, navigate, goHome]);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");
    setDevCode("");
    setLoading(true);
    try {
      const res = await apiClient.post("/auth/email-step", { email });
      if (res.data.flow === "password") {
        setStep("password");
      } else {
        setStep("code");
        setInfo(res.data.message || "Code wurde versendet.");
        if (res.data.dev_code) {
          setDevCode(res.data.dev_code);
        }
      }
    } catch (e) {
      setError(e.response?.data?.detail || "E-Mail konnte nicht geprüft werden.");
    } finally {
      setLoading(false);
    }
  };

  // Trusted-Device-Token Helper (lokal pro E-Mail gespeichert)
  const tdKey = (em) => `trustedDeviceToken_${(em || "").toLowerCase()}`;
  const getTrustedDeviceToken = (em) => {
    try { return localStorage.getItem(tdKey(em)) || undefined; } catch { return undefined; }
  };
  const saveTrustedDeviceToken = (em, t) => {
    try { if (t) localStorage.setItem(tdKey(em), t); } catch { /* ignore */ }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await apiClient.post("/auth/login-password", {
        email,
        password,
        trusted_device_token: getTrustedDeviceToken(email),
      });
      if (res.data.requires_2fa) {
        setPendingToken(res.data.pending_token);
        setStep("totp");
        setInfo("6-stelligen Code aus deiner Authenticator-App eingeben (oder einen Backup-Code).");
        return;
      }
      localStorage.removeItem("authToken");
      localStorage.setItem("authEmail", email);
      setEmail(email);
      await goHome();
    } catch (e) {
      setError(e.response?.data?.detail || "Anmeldung fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  };

  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await apiClient.post("/auth/verify-code", {
        email,
        code,
        trusted_device_token: getTrustedDeviceToken(email),
      });
      if (res.data.requires_2fa) {
        setPendingToken(res.data.pending_token);
        setStep("totp");
        setInfo("6-stelligen Code aus deiner Authenticator-App eingeben (oder einen Backup-Code).");
        return;
      }
      localStorage.removeItem("authToken");
      localStorage.setItem("authEmail", email);
      setEmail(email);
      if (res.data.requires_profile) {
        setStep("profile");
      } else {
        await goHome();
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Code-Prüfung fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  };

  const handleTotpSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await apiClient.post("/auth/2fa-verify-login", {
        pending_token: pendingToken,
        code: totpCode.trim(),
        remember_device: rememberDevice,
      });
      if (res.data.trusted_device_token) {
        saveTrustedDeviceToken(email, res.data.trusted_device_token);
      }
      localStorage.removeItem("authToken");
      localStorage.setItem("authEmail", email);
      setEmail(email);
      await goHome();
    } catch (e) {
      setError(e.response?.data?.detail || "2FA-Code ist ungültig.");
    } finally {
      setLoading(false);
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await apiClient.post("/auth/complete-profile", { name: profileName });
      await refreshMe();
      await goHome();
    } catch (e) {
      setError(e.response?.data?.detail || "Profil konnte nicht gespeichert werden.");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError("");
    setInfo("");
    setResending(true);
    try {
      const res = await apiClient.post("/auth/resend-code", { email });
      setInfo(res.data.message || "Neuer Code versendet.");
      if (res.data.dev_code) {
        setDevCode(res.data.dev_code);
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Code konnte nicht erneut gesendet werden.");
    } finally {
      setResending(false);
    }
  };

  const goBack = () => {
    setError("");
    setInfo("");
    setCode("");
    setTotpCode("");
    setPendingToken("");
    setPassword("");
    setDevCode("");
    setStep("email");
  };

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="login-page">
      {/* Left: Brand panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-slate-900 text-slate-50 p-12 flex-col justify-between relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute -top-20 -right-20 h-80 w-80 rounded-full bg-emerald-500 blur-3xl" />
          <div className="absolute -bottom-20 -left-20 h-80 w-80 rounded-full bg-sky-500 blur-3xl" />
        </div>
        <div className="relative">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center border border-white/20">
              <Building2 className="h-6 w-6" />
            </div>
            <div>
              <div className="text-[11px] font-semibold tracking-widest text-slate-400 uppercase">
                CH-GmbH
              </div>
              <div className="text-lg font-semibold">Buchhaltung</div>
            </div>
          </div>
        </div>
        <div className="relative space-y-6">
          <h2 className="text-4xl font-semibold tracking-tight leading-tight" data-testid="login-headline">
            {isTrustee ? "Treuhänder-Portal." : "Sicherer Zugang per Einmal-Code."}
          </h2>
          <p className="text-sm text-slate-300 max-w-md">
            {isTrustee
              ? "Alle Ihre Mandanten und offenen Prüfungen an einem Ort. Anmeldung mit der E-Mail, mit der Sie als Treuhänder eingeladen wurden."
              : "Kein Passwort, kein Spam — nur freigegebene E-Mails kommen rein. Ihr Admin verwaltet die Zugriffe."}
          </p>
          <ul className="space-y-3">
            {(isTrustee ? TRUSTEE_BENEFITS : BENEFITS).map((b) => (
              <li key={b} className="flex items-start gap-2.5 text-sm text-slate-300">
                <CheckCircle2 className="h-4 w-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="relative text-xs text-slate-400 space-y-1.5">
          <div>© {new Date().getFullYear()} · Schweizer Buchhaltungssoftware für KMUs</div>
          <div data-testid="login-powered-by">
            Powered by{" "}
            <a
              href="https://www.invictaflow.ch"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-slate-300 hover:text-white underline-offset-2 hover:underline"
            >
              InvictaX Group GmbH
            </a>
            {" · "}
            <Link to="/impressum" className="hover:text-slate-200">Impressum</Link>
            {" · "}
            <Link to="/plattformen" className="hover:text-slate-200">Unsere Plattformen</Link>
          </div>
        </div>
      </div>

      {/* Right: Form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <Card className="w-full max-w-md p-8 border border-slate-200 shadow-sm bg-white">
          <div className="lg:hidden flex items-center gap-2 mb-6">
            <div className="h-9 w-9 rounded-lg bg-slate-900 text-white flex items-center justify-center">
              <Building2 className="h-5 w-5" />
            </div>
            <div className="font-semibold text-slate-900">CH-Buchhaltung</div>
          </div>

          {/* Step indicator */}
          <div className="flex items-center gap-2 mb-5 text-xs" data-testid="login-stepper">
            <StepDot active={step === "email"} done={step !== "email"} icon={Mail} label="E-Mail" />
            <div className="flex-1 h-px bg-slate-200" />
            <StepDot
              active={step === "password" || step === "code"}
              done={step === "profile"}
              icon={step === "password" ? ShieldCheck : KeyRound}
              label={step === "password" ? "Passwort" : "Code"}
            />
            <div className="flex-1 h-px bg-slate-200" />
            <StepDot active={step === "profile"} done={false} icon={UserRound} label="Profil" />
          </div>

          {error && (
            <div
              className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800"
              data-testid="login-error"
            >
              {error}
            </div>
          )}
          {info && (
            <div
              className="mb-4 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800"
              data-testid="login-info"
            >
              {info}
            </div>
          )}
          {devCode && (
            <div
              className="mb-4 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
              data-testid="login-dev-code"
            >
              <strong>Dev-Hinweis (SMTP nicht konfiguriert):</strong>{" "}
              <code className="font-mono">{devCode}</code>
            </div>
          )}

          {step === "email" && (
            <>
              <h1 className="text-2xl font-semibold text-slate-900 mb-1">Anmelden</h1>
              <p className="text-sm text-slate-500 mb-6">
                Bitte geben Sie Ihre E-Mail-Adresse ein. Wir senden Ihnen einen einmaligen Anmeldecode.
              </p>
              <form
                className="space-y-4"
                onSubmit={handleEmailSubmit}
                data-testid="login-form"
              >
                <div className="space-y-1.5">
                  <Label htmlFor="email">E-Mail</Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    autoFocus
                    value={email}
                    onChange={(e) => setEmailLocal(e.target.value)}
                    data-testid="login-email-input"
                    required
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full rounded-full bg-slate-900 text-slate-50 hover:bg-slate-800"
                  disabled={loading}
                  data-testid="login-submit-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Prüfe E-Mail …
                    </>
                  ) : (
                    "Weiter"
                  )}
                </Button>
              </form>
            </>
          )}

          {step === "password" && (
            <>
              <h1 className="text-2xl font-semibold text-slate-900 mb-1">
                Administrator-Anmeldung
              </h1>
              <p className="text-sm text-slate-500 mb-6">
                Bitte geben Sie Ihr Passwort ein. <strong>{email}</strong>
              </p>
              <form
                className="space-y-4"
                onSubmit={handlePasswordSubmit}
                data-testid="login-password-form"
              >
                <div className="space-y-1.5">
                  <Label htmlFor="password">Passwort</Label>
                  <Input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    autoFocus
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="login-password-input"
                    required
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    className="rounded-full"
                    onClick={goBack}
                    data-testid="login-back-button"
                  >
                    <ArrowLeft className="h-4 w-4 mr-1" /> Zurück
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 rounded-full bg-slate-900 text-slate-50 hover:bg-slate-800"
                    disabled={loading}
                    data-testid="login-password-submit-button"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Anmelden …
                      </>
                    ) : (
                      "Anmelden"
                    )}
                  </Button>
                </div>
              </form>
            </>
          )}

          {step === "code" && (
            <>
              <h1 className="text-2xl font-semibold text-slate-900 mb-1">Code eingeben</h1>
              <p className="text-sm text-slate-500 mb-6">
                Wir haben einen 6-stelligen Code an <strong>{email}</strong> gesendet.
                Der Code ist 10 Minuten gültig.
              </p>
              <form
                className="space-y-4"
                onSubmit={handleCodeSubmit}
                data-testid="login-code-form"
              >
                <div className="space-y-1.5">
                  <Label htmlFor="code">Anmeldecode</Label>
                  <Input
                    id="code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    autoFocus
                    maxLength={6}
                    pattern="\d{6}"
                    placeholder="123456"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    className="text-center text-2xl tracking-[0.5em] font-mono"
                    data-testid="login-code-input"
                    required
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    className="rounded-full"
                    onClick={goBack}
                    data-testid="login-back-button"
                  >
                    <ArrowLeft className="h-4 w-4 mr-1" /> Zurück
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 rounded-full bg-slate-900 text-slate-50 hover:bg-slate-800"
                    disabled={loading || code.length !== 6}
                    data-testid="login-code-submit-button"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Prüfe Code …
                      </>
                    ) : (
                      "Bestätigen"
                    )}
                  </Button>
                </div>
                <button
                  type="button"
                  className="text-xs text-slate-500 hover:text-slate-900 hover:underline disabled:opacity-50"
                  onClick={handleResend}
                  disabled={resending}
                  data-testid="login-resend-button"
                >
                  {resending ? "Wird gesendet …" : "Keinen Code erhalten? Erneut senden"}
                </button>
              </form>
            </>
          )}

          {step === "totp" && (
            <>
              <h1 className="text-2xl font-semibold text-slate-900 mb-1">
                Zwei-Faktor-Authentifizierung
              </h1>
              <p className="text-sm text-slate-500 mb-6">
                {info || "6-stelligen Code aus deiner Authenticator-App eingeben (oder einen Backup-Code)."}
              </p>
              <form
                className="space-y-4"
                onSubmit={handleTotpSubmit}
                data-testid="login-totp-form"
              >
                <div className="space-y-1.5">
                  <Label htmlFor="totp-code">Code</Label>
                  <Input
                    id="totp-code"
                    type="text"
                    inputMode="text"
                    autoFocus
                    placeholder="123 456 oder BACKUP-CODE"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value)}
                    data-testid="login-totp-code-input"
                    className="tracking-widest text-lg text-center font-mono"
                  />
                </div>
                <label className="flex items-start gap-2.5 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={rememberDevice}
                    onChange={(e) => setRememberDevice(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                    data-testid="login-remember-device-checkbox"
                  />
                  <span className="text-sm text-slate-600">
                    <span className="font-medium text-slate-800">Diesem Gerät 30 Tage vertrauen</span>
                    <span className="block text-xs text-slate-500 mt-0.5">
                      Auf diesem Browser wird der Code-Schritt für 30 Tage übersprungen. Nicht auf öffentlichen Geräten aktivieren.
                    </span>
                  </span>
                </label>
                {error && (
                  <p className="text-sm text-rose-600" data-testid="login-error">
                    {error}
                  </p>
                )}
                <Button
                  type="submit"
                  disabled={loading || !totpCode}
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white"
                  data-testid="login-totp-submit-button"
                >
                  {loading ? "Prüfe …" : "Anmelden"}
                </Button>
                <button
                  type="button"
                  onClick={goBack}
                  className="w-full text-xs text-slate-500 hover:text-slate-700"
                  data-testid="login-totp-back"
                >
                  ← Zurück zum Login
                </button>
              </form>
            </>
          )}

          {step === "profile" && (
            <>
              <h1 className="text-2xl font-semibold text-slate-900 mb-1">
                Willkommen!
              </h1>
              <p className="text-sm text-slate-500 mb-6">
                Vervollständigen Sie kurz Ihr Profil. Diesen Schritt machen Sie nur einmal.
              </p>
              <form
                className="space-y-4"
                onSubmit={handleProfileSubmit}
                data-testid="login-profile-form"
              >
                <div className="space-y-1.5">
                  <Label htmlFor="profile-name">Ihr Name</Label>
                  <Input
                    id="profile-name"
                    type="text"
                    autoFocus
                    placeholder="z.B. Max Muster"
                    value={profileName}
                    onChange={(e) => setProfileName(e.target.value)}
                    data-testid="login-profile-name-input"
                    minLength={2}
                    required
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full rounded-full bg-slate-900 text-slate-50 hover:bg-slate-800"
                  disabled={loading || profileName.trim().length < 2}
                  data-testid="login-profile-submit-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Speichere …
                    </>
                  ) : (
                    "Profil speichern & loslegen"
                  )}
                </Button>
              </form>
            </>
          )}

          <div className="mt-8 pt-5 border-t border-slate-100 text-[11px] text-slate-400 leading-relaxed">
            <ShieldCheck className="inline h-3 w-3 mr-0.5 text-emerald-600" />
            Sicherer passwortloser Zugang. Nur freigegebene E-Mails erhalten einen Code.
          </div>
          <div className="mt-4 text-center text-sm text-slate-600">
            {registrationOpen ? (
              <>
                Noch kein Konto?{" "}
                <a
                  href="/register"
                  className="text-indigo-600 hover:text-indigo-800 hover:underline font-semibold"
                  data-testid="login-register-link"
                >
                  → Hier registrieren
                </a>
              </>
            ) : (
              <span
                className="text-xs text-slate-400"
                data-testid="login-register-disabled-hint"
              >
                Registrierung aktuell nur per Einladung
              </span>
            )}
          </div>
          {!isTrustee && (
            <div className="mt-2 text-center text-xs text-slate-500">
              Sie sind Treuhänder?{" "}
              <a href="/treuhand" className="text-indigo-600 hover:text-indigo-800 hover:underline font-medium" data-testid="login-trustee-link">
                Zum Treuhänder-Login →
              </a>
            </div>
          )}
          <div className="mt-2 text-center text-xs text-slate-500">
            Treuhänder oder ERP-Anbieter?{" "}
            <a
              href="/partner"
              className="text-indigo-600 hover:text-indigo-800 hover:underline font-medium"
              data-testid="login-partner-link"
            >
              Für Partner →
            </a>
          </div>
        </Card>
      </div>
    </div>
  );
}

function StepDot({ active, done, icon: Icon, label }) {
  return (
    <div className="flex items-center gap-1.5">
      <div
        className={`h-7 w-7 rounded-full flex items-center justify-center transition-colors ${
          done
            ? "bg-emerald-600 text-white"
            : active
              ? "bg-slate-900 text-white"
              : "bg-slate-100 text-slate-400"
        }`}
      >
        {done ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Icon className="h-3.5 w-3.5" />}
      </div>
      <span
        className={`text-[11px] ${active ? "font-medium text-slate-900" : "text-slate-400"}`}
      >
        {label}
      </span>
    </div>
  );
}
